import { motion, TargetAndTransition, Transition, VariantLabels } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

type AnimateOnScrolledToProps = {
  children: React.ReactNode
  className?: string
  scrollTriggerPercentage?: number
  triggerPosition?: 'top' | 'center' | 'bottom'
  from?: TargetAndTransition | VariantLabels
  to?: TargetAndTransition | VariantLabels
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transition?: Transition<any>
}

/**
 * AnimateOnScrolledTo component animates its children when they are scrolled into view.
 *
 * By default, it fades in and slides up its content from 40px below, but the animation can be customized
 * via the `from`, `to`, and `transition` props. The animation is triggered when the component's top edge
 * enters a specified percentage of the viewport height (default is 60%).
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The content to animate when scrolled into view.
 * @param {string} [props.className] - Optional additional CSS classes for the container.
 * @param {number} [props.scrollTriggerPercentage=0.96] - The percentage of the viewport height from the top at which to trigger the animation (0 to 1).
 * @param {'top' | 'center' | 'bottom'} [props.triggerPosition='bottom'] - The position of the component to use as the trigger point for the animation.
 * @param {TargetAndTransition | VariantLabels} [props.from={ opacity: 0, y: 40 }] - The initial animation state.
 * @param {TargetAndTransition | VariantLabels} [props.to={ opacity: 1, y: 0 }] - The target animation state when visible.
 * @param {Transition<any>} [props.transition={ duration: 0.5 }] - The transition configuration for the animation.
 *
 * @returns {JSX.Element} A motion.div that animates its children when scrolled into view.
 */
export const AnimateOnScrolledTo = ({
  children,
  className = '',
  scrollTriggerPercentage = 0.96,
  triggerPosition = 'bottom',
  from = { opacity: 0, y: 40 },
  to = { opacity: 1, y: 0 },
  transition = { duration: 0.5 },
}: AnimateOnScrolledToProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return
    const handleScroll = () => {
      const rect = node.getBoundingClientRect()
      // Calculate the trigger point based on the specified scrollTriggerPercentage
      const trigger = window.innerHeight * scrollTriggerPercentage
      let triggerPoint = rect.top
      if (triggerPosition === 'center') {
        triggerPoint = rect.top + rect.height / 2
      } else if (triggerPosition === 'bottom') {
        triggerPoint = rect.bottom
      }
      if (triggerPoint < trigger) {
        setVisible(true)
      } else {
        setVisible(false)
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [scrollTriggerPercentage, triggerPosition])

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={from}
      animate={visible ? to : from}
      transition={transition}
    >
      {children}
    </motion.div>
  )
}
